import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState, useCallback } from "react";
import { ArrowUpRight, Target, Layers, MapPin, BarChart3, Clock, Rocket, Compass, Banknote, Users, Activity, ChevronLeft, ChevronRight } from "lucide-react";

const torontoImg = "https://images.unsplash.com/photo-1543962226-818f4301073f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxUb3JvbnRvJTIwc2t5bGluZSUyMENOJTIwVG93ZXIlMjBldmVuaW5nfGVufDB8fHx8MTc3NTU4NjQ4MXww&ixlib=rb-4.1.0&q=85";

const investAreas = [
  "Financial infrastructure and regulated payments",
  "Vertical B2B software and enterprise systems",
  "Applied operational systems in regulated environments",
  "Security, compliance, and risk tooling",
  "Platforms with strong unit economics and structured growth models",
];

const executionAreas = [
  { icon: Rocket, title: "Go-to-market", desc: "Customer segmentation, pricing strategy, sales structure, partnership development, and pipeline discipline" },
  { icon: Compass, title: "Strategy", desc: "Prioritization, sequencing of initiatives, and performance metrics aligned with capital objectives" },
  { icon: Banknote, title: "Fundraising", desc: "Narrative clarity, positioning, data room preparation, and investor mapping" },
  { icon: Users, title: "Hiring", desc: "Definition of critical roles, interview structure, and compensation frameworks" },
  { icon: Activity, title: "Operating cadence", desc: "KPI systems, reporting standards, board governance, and decision-making discipline" },
];

const frameworks = [
  { icon: MapPin, title: "Canadian foundation", desc: "Proximity to domestic markets and regulatory frameworks informs our underwriting and capital allocation, complemented by a cross-border perspective supporting expansion into the United States and selected international markets." },
  { icon: Target, title: "Thesis-led investing", desc: "Our investment strategy is focused on regulated and infrastructure-led markets. We concentrate capital where structural complexity supports defensibility and long-term value creation." },
  { icon: Layers, title: "Concentrated portfolio model", desc: "We operate with selectivity and deploy capital in a limited number of companies. This enables disciplined underwriting and meaningful engagement with leadership teams." },
  { icon: BarChart3, title: "Investment-led engagement", desc: "We work directly with portfolio companies on governance standards, capital allocation discipline, and operating clarity. Engagement is aligned with capital deployment and measured against defined objectives." },
  { icon: Clock, title: "Long-term capital horizon", desc: "We invest with a multi-year perspective and participate selectively in follow-on rounds where execution supports conviction." },
];

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function AboutUs() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const block2Ref = useRef(null);
  const block2InView = useInView(block2Ref, { once: true, margin: "-80px" });
  const block3Ref = useRef(null);
  const block3InView = useInView(block3Ref, { once: true, margin: "-80px" });
  const block4Ref = useRef(null);
  const block4InView = useInView(block4Ref, { once: true, margin: "-80px" });
  const ctaRef = useRef(null);
  const ctaInView = useInView(ctaRef, { once: true, margin: "-80px" });

  // Framework rail scroll state
  const railRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  }, []);

  const scrollRail = useCallback((dir) => {
    const el = railRef.current;
    if (!el) return;
    const card = el.querySelector('[data-fw-card]');
    const cardW = card ? card.offsetWidth + 24 : 440;
    el.scrollBy({ left: dir * cardW, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  return (
    <div className="min-h-screen bg-white" data-testid="about-us-page">
      <Navbar />

      {/* ============================================================ */}
      {/* HERO — Georgian.io-inspired 3-column bento layout             */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden pt-28 md:pt-36 pb-[45px] md:pb-[75px]" data-testid="about-hero" style={{ background: 'linear-gradient(180deg, #f8faff 0%, #f3f5fb 50%, #f8faff 100%)' }}>
        {/* Subtle grid */}
        <div className="absolute inset-0 grid-pattern opacity-[0.06]" />

        {/* Left radial glow */}
        <motion.div
          className="absolute pointer-events-none"
          style={{ top: '5%', left: '-8%' }}
          animate={{ x: [0, 10, 0], y: [0, -6, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-[500px] h-[600px] md:w-[600px] md:h-[700px] rounded-full blur-[140px]"
            style={{ background: 'radial-gradient(circle, rgba(79,107,255,0.12) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)' }}
          />
        </motion.div>

        {/* Right radial glow */}
        <motion.div
          className="absolute pointer-events-none"
          style={{ bottom: '-10%', right: '-6%' }}
          animate={{ x: [0, -8, 0], y: [0, 10, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="w-[400px] h-[500px] md:w-[500px] md:h-[600px] rounded-full blur-[140px]"
            style={{ background: 'radial-gradient(circle, rgba(123,97,255,0.10) 0%, rgba(79,107,255,0.04) 50%, transparent 75%)' }}
          />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-[50px] md:mb-[60px]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="flex items-center justify-center gap-3 mb-8"
            >
              <div className="w-8 h-px bg-[#4F6BFF]" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[#4F6BFF]/80">About Us</span>
              <div className="w-8 h-px bg-[#4F6BFF]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-6"
              data-testid="about-page-title"
            >
              About Nebula Flow
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.22 }}
              className="font-body text-[15px] md:text-base leading-[1.75] text-[#475569] max-w-[780px] mx-auto"
            >
              A Canadian venture capital firm concentrating capital in early-stage companies across regulated{' '}
              <br className="hidden md:inline" />
              and infrastructure-led markets, with disciplined underwriting and long-term alignment.
            </motion.p>
          </div>

          {/* Frosted glass container — Georgian.io style */}
          <div
            className="rounded-[2rem] p-8 md:p-12 border border-white/60"
            style={{
              background: 'linear-gradient(145deg, rgba(255,255,255,0.65) 0%, rgba(248,250,255,0.50) 100%)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              boxShadow: '0 10px 40px rgba(15,23,42,0.06), 0 2px 8px rgba(15,23,42,0.03), inset 0 1px 0 rgba(255,255,255,0.5)',
            }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-7">

              {/* === LEFT: Asymmetric two-column card layout === */}
              <div className="lg:col-span-7 flex flex-col lg:flex-row gap-5">

                {/* Left column: tall Cross-Border + short Concentrated */}
                <div className="flex-1 flex flex-col gap-5 min-w-0">
                  {/* Cross-Border Perspective — footer-style dark bg + orbit rings */}
                  <AnimatedSection delay={0.2}>
                    <div
                      className="rounded-2xl overflow-hidden relative min-h-[220px]"
                      style={{ boxShadow: '0 4px 16px rgba(15,23,42,0.08)', background: 'linear-gradient(160deg, #08122a 0%, #091530 45%, #0b1a3a 100%)' }}
                      data-testid="about-card-crossborder"
                    >
                      {/* Top edge highlight — like footer */}
                      <div className="absolute top-0 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-slate-500/15 to-transparent" />
                      {/* Pulsing glow — contained, centered */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <motion.div
                          className="w-[180px] h-[180px] rounded-full"
                          style={{ background: 'radial-gradient(circle, rgba(79,107,255,0.20) 0%, rgba(123,97,255,0.07) 50%, transparent 70%)' }}
                          animate={{ scale: [1, 1.12, 1], opacity: [0.5, 0.9, 0.5] }}
                          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                        />
                      </div>
                      {/* Orbit rings — contained within card */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <svg className="w-[200px] h-[200px]" viewBox="0 0 200 200" fill="none">
                          <circle cx="100" cy="100" r="95" stroke="rgba(79,107,255,0.20)" strokeWidth="0.7" />
                          <circle cx="100" cy="100" r="68" stroke="rgba(123,97,255,0.16)" strokeWidth="0.7" />
                          <circle cx="100" cy="100" r="40" stroke="rgba(79,107,255,0.12)" strokeWidth="0.7" />
                        </svg>
                      </div>
                      {/* Slow-orbiting dot — contained */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <motion.div
                          className="relative w-[190px] h-[190px]"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
                        >
                          <div
                            className="absolute w-[5px] h-[5px] rounded-full"
                            style={{ top: 0, left: 'calc(50% - 2.5px)', background: '#6B8AFF', boxShadow: '0 0 12px rgba(79,107,255,0.7), 0 0 30px rgba(79,107,255,0.25)' }}
                          />
                        </motion.div>
                      </div>
                      {/* Second orbiting dot — opposite direction */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <motion.div
                          className="relative w-[136px] h-[136px]"
                          animate={{ rotate: -360 }}
                          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                        >
                          <div
                            className="absolute w-[4px] h-[4px] rounded-full"
                            style={{ bottom: 0, left: 'calc(50% - 2px)', background: '#9B8AFF', boxShadow: '0 0 10px rgba(123,97,255,0.6)' }}
                          />
                        </motion.div>
                      </div>
                      <div className="relative z-10 h-full flex flex-col justify-end p-6">
                        <h3 className="font-body text-lg font-semibold text-white tracking-tight leading-snug mb-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>Cross-Border Perspective</h3>
                        <p className="font-body text-sm text-white/70 leading-relaxed lg:whitespace-nowrap">Canada-based with international reach</p>
                      </div>
                    </div>
                  </AnimatedSection>

                  {/* Concentrated Portfolio — short card, no border, light shadow */}
                  <AnimatedSection delay={0.3}>
                    <div
                      className="rounded-2xl p-5 flex flex-col justify-between relative overflow-hidden min-h-[148px]"
                      style={{ boxShadow: '0 6px 22px rgba(15,23,42,0.09), 0 2px 6px rgba(15,23,42,0.04)', backdropFilter: 'blur(8px)', background: 'linear-gradient(135deg, rgba(243,246,255,0.95) 0%, rgba(235,240,255,0.90) 50%, rgba(240,243,255,0.95) 100%)' }}
                      data-testid="about-card-concentrated"
                    >
                      <div className="relative z-10">
                        <h3 className="font-body text-lg font-semibold text-[#08122a] tracking-tight leading-snug mb-1">Concentrated Portfolio</h3>
                        <p className="font-body text-sm text-[#475569]">Selective deployment across a limited number of companies</p>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

                {/* Right column: short Seed & Series A + tall Proudly Canadian */}
                <div className="flex-1 flex flex-col gap-5 min-w-0">
                  {/* Seed & Series A — short card */}
                  <AnimatedSection delay={0.25}>
                    <div
                      className="rounded-2xl p-5 bg-white/70 border border-white/80 relative overflow-hidden min-h-[148px]"
                      style={{ boxShadow: '0 4px 16px rgba(15,23,42,0.06)', backdropFilter: 'blur(8px)' }}
                      data-testid="about-card-seed"
                    >
                      <h3 className="font-body text-lg font-semibold text-[#08122a] tracking-tight leading-snug mb-1 relative z-10">Seed & Series A</h3>
                      <p className="font-body text-sm text-[#475569] relative z-10">Focused early-stage capital</p>
                      {/* Growth curve — animated draw + traveling dot */}
                      <svg className="absolute bottom-0 left-0 right-0 h-[40%] pointer-events-none" style={{ width: '100%' }} viewBox="0 0 1000 400" preserveAspectRatio="none" fill="none">
                        <defs>
                          <linearGradient id="seedCurveStroke" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#061b5e" />
                            <stop offset="100%" stopColor="#2a57bf" />
                          </linearGradient>
                          <linearGradient id="seedCurveFill" x1="0" y1="0" x2="1000" y2="0" gradientUnits="userSpaceOnUse">
                            <stop offset="0%" stopColor="#061b5e" />
                            <stop offset="100%" stopColor="#2a57bf" />
                          </linearGradient>
                        </defs>
                        <motion.path
                          d="M -10 380 C 150 360 300 300 450 230 C 600 160 750 80 880 35 C 930 18 970 6 1010 -5 L 1010 410 L -10 410 Z"
                          fill="url(#seedCurveFill)"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 0.05 }}
                          transition={{ duration: 1.5, delay: 0.8 }}
                        />
                        <motion.path
                          d="M -10 380 C 150 360 300 300 450 230 C 600 160 750 80 880 35 C 930 18 970 6 1010 -5"
                          stroke="url(#seedCurveStroke)"
                          strokeWidth="12"
                          fill="none"
                          initial={{ pathLength: 0, opacity: 0.3 }}
                          animate={{ pathLength: 1, opacity: 0.3 }}
                          transition={{ duration: 2, delay: 0.5, ease: "easeOut" }}
                        />
                        <circle r="8" fill="#2a57bf" opacity="0.5">
                          <animateMotion dur="4s" repeatCount="indefinite" path="M -10 380 C 150 360 300 300 450 230 C 600 160 750 80 880 35 C 930 18 970 6 1010 -5" />
                          <animate attributeName="opacity" values="0.5;0.2;0.5" dur="2s" repeatCount="indefinite" />
                        </circle>
                      </svg>
                    </div>
                  </AnimatedSection>

                  {/* Proudly Canadian — tall card */}
                  <AnimatedSection delay={0.35}>
                    <div
                      className="rounded-2xl overflow-hidden relative min-h-[220px]"
                      style={{ boxShadow: '0 8px 24px rgba(15,23,42,0.10), 0 2px 6px rgba(15,23,42,0.04)' }}
                      data-testid="about-card-canadian"
                    >
                      <img src={torontoImg} alt="Canadian cityscape" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 40%, rgba(0,0,0,0.65) 100%)' }} />
                      <div className="absolute bottom-0 left-0 right-0 z-10 p-6">
                        <h3 className="font-body text-lg font-semibold text-white tracking-tight leading-snug mb-1" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.4)' }}>Proudly Canadian</h3>
                        <p className="font-body text-[13px] text-white/60">Deep local presence with global perspective</p>
                      </div>
                    </div>
                  </AnimatedSection>
                </div>

              </div>

              {/* === RIGHT: Text block (wider) === */}
              <div className="lg:col-span-5 flex flex-col justify-center">
                <motion.h1
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className="font-body text-[28px] sm:text-[45px] leading-[1.1] text-[#08122a] tracking-tight font-semibold mb-5"
                  data-testid="about-heading"
                >
                  A focused early-stage<br />investment platform
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="font-body text-[14px] md:text-[15px] leading-[1.75] text-[#475569] mb-4">
                    Nebula Flow is a <strong className="text-[#08122a] font-semibold">Canadian venture capital firm</strong> operating in regulated and infrastructure-led markets. We concentrate capital in a limited number of early-stage companies, prioritizing <strong className="text-[#08122a] font-semibold">disciplined underwriting</strong>, repeatable distribution, and durable value creation.
                  </p>
                  <p className="font-body text-[14px] md:text-[15px] leading-[1.75] text-[#475569]">
                    Our model is built to maintain <strong className="text-[#08122a] font-semibold">depth of engagement</strong> and long-term alignment from day one.
                  </p>
                </motion.div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* BLOCK 2: What we invest in                                    */}
      {/* ============================================================ */}
      <section
        className="relative py-[30px] md:py-[50px]"
        data-testid="about-invest-in"
        ref={block2Ref}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={block2InView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="structured-bg wave-lines relative rounded-[2rem] overflow-hidden p-8 md:p-12 lg:p-14"
          >
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-[33px] lg:gap-20 items-start">
              <div>
                <h2 className="font-body text-[30px] sm:text-[45px] tracking-tight leading-[1.1] font-semibold text-white mb-5">What we invest in</h2>
                <p className="font-body text-base md:text-lg leading-relaxed text-indigo-100/80">
                  Nebula Flow invests in early-stage technology companies with defined customers, validated demand, and a clear commercial pathway. We prioritize businesses with scalable distribution, disciplined capital planning, and defensible positioning.
                </p>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={block2InView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <ul className="space-y-5">
                  {investAreas.map((area, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={block2InView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-white/40" />
                      <span className="font-body text-[15px] text-white/90 leading-relaxed">{area}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* BLOCK 3: Execution focus                                      */}
      {/* ============================================================ */}
      <section className="relative py-[30px] md:py-[50px] overflow-hidden" data-testid="about-execution" ref={block3Ref}>
        {/* Very subtle orbital in background */}
        <div className="absolute top-[-20%] left-[-10%] pointer-events-none hidden md:block opacity-[0.04]">
          <svg width="600" height="600" viewBox="0 0 600 600" fill="none">
            <circle cx="300" cy="300" r="290" stroke="#4F6BFF" strokeWidth="1" />
            <circle cx="300" cy="300" r="200" stroke="#7B61FF" strokeWidth="1" />
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={block3InView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="mb-[36px] md:mb-14 text-center"
          >
            <h2 className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-5">Execution focus</h2>
            <p className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-2xl mx-auto">
              We engage directly with leadership teams across core execution areas:
            </p>
          </motion.div>
          {/* Top row: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {executionAreas.slice(0, 3).map((area, i) => (
              <motion.div
                key={area.title}
                data-testid={`execution-card-${i + 1}`}
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={block3InView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
                className="card-gradient-line rounded-2xl border border-black/[0.04] p-7 cursor-default group bg-white"
                style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100/60 flex items-center justify-center mb-4">
                  <area.icon className="w-[18px] h-[18px] text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-body text-[16px] font-semibold text-[#08122a] mb-2.5 tracking-tight">{area.title}</h3>
                <p className="font-body text-sm leading-relaxed text-[#475569]">{area.desc}</p>
              </motion.div>
            ))}
          </div>
          {/* Bottom row: 2 cards — full width */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
            {executionAreas.slice(3).map((area, i) => (
              <motion.div
                key={area.title}
                data-testid={`execution-card-${i + 4}`}
                initial={{ opacity: 0, y: 60, scale: 0.95 }}
                animate={block3InView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.15 * (i + 3), ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(15,23,42,0.12), 0 4px 12px rgba(15,23,42,0.06)", transition: { duration: 0.15 } }}
                className="card-gradient-line rounded-2xl border border-black/[0.04] p-7 cursor-default group bg-white"
                style={{ boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100/60 flex items-center justify-center mb-4">
                  <area.icon className="w-[18px] h-[18px] text-blue-600" strokeWidth={1.5} />
                </div>
                <h3 className="font-body text-[16px] font-semibold text-[#08122a] mb-2.5 tracking-tight">{area.title}</h3>
                <p className="font-body text-sm leading-relaxed text-[#475569]">{area.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* BLOCK 4: Investment framework — horizontal rail               */}
      {/* ============================================================ */}
      <section
        className="relative py-[30px] md:py-[50px] overflow-hidden"
        data-testid="about-framework"
        ref={block4Ref}
      >
        {/* Header: heading + arrows */}
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={block4InView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="flex items-end justify-between mb-[28px] md:mb-12"
          >
            <div>
              <p className="font-body text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#5B6BCC' }}>Our approach</p>
              <h2 className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a]">
                Investment framework
              </h2>
            </div>
            <div className="flex gap-2 mb-1">
              <button
                onClick={() => scrollRail(-1)}
                disabled={!canScrollLeft}
                className="w-10 h-10 rounded-full border border-slate-300 bg-white flex items-center justify-center transition-all duration-200 hover:border-slate-400 hover:shadow-sm disabled:opacity-30 disabled:cursor-default"
                data-testid="framework-arrow-left"
              >
                <ChevronLeft className="w-[18px] h-[18px] text-slate-600" strokeWidth={2} />
              </button>
              <button
                onClick={() => scrollRail(1)}
                disabled={!canScrollRight}
                className="w-10 h-10 rounded-full border border-slate-300 bg-white flex items-center justify-center transition-all duration-200 hover:border-slate-400 hover:shadow-sm disabled:opacity-30 disabled:cursor-default"
                data-testid="framework-arrow-right"
              >
                <ChevronRight className="w-[18px] h-[18px] text-slate-600" strokeWidth={2} />
              </button>
            </div>
          </motion.div>

          {/* Scrollable rail — aligned with heading */}
          <div
            ref={railRef}
            className="flex gap-5 overflow-x-auto scrollbar-hide"
            style={{
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {frameworks.map((fw, i) => (
              <motion.div
                key={fw.title}
                data-fw-card
                data-testid={`framework-item-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                animate={block4InView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                className="flex-shrink-0 rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-default w-[calc(100vw-48px)] max-w-[380px] md:w-[380px]"
                style={{
                  scrollSnapAlign: 'start',
                  background: 'linear-gradient(135deg, #2563EB 0%, #4338CA 50%, #3730A3 100%)',
                  boxShadow: '0 8px 32px rgba(37,99,235,0.18), 0 2px 8px rgba(0,0,0,0.06)',
                }}
              >
                <h3 className="font-body text-lg font-semibold text-white mb-3 tracking-tight">{fw.title}</h3>
                <p className="font-body text-sm leading-relaxed text-white/75">{fw.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* BLOCK 5: Connect CTA — with orbital elements like homepage    */}
      {/* ============================================================ */}
      <section className="pt-[30px] pb-[60px] md:py-[50px]" data-testid="about-cta" ref={ctaRef}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={ctaInView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative rounded-[2rem] overflow-hidden py-12 md:py-16 px-8 md:px-16 text-center border border-black/[0.04]"
            style={{ background: 'linear-gradient(180deg, #f8faff 0%, #f0f4ff 50%, #f5f7fc 100%)', boxShadow: '0 10px 30px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)' }}
          >
            {/* Subtle grid */}
            <div className="absolute inset-0 grid-pattern opacity-10" />

            {/* Vignette */}
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, transparent 30%, rgba(15,23,42,0.025) 100%)' }}
            />

            {/* Left circle */}
            <div className="absolute pointer-events-none hidden md:block" style={{ top: '15%', left: '-12%' }}>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] md:w-[420px] md:h-[420px] rounded-full blur-[90px]"
                style={{ background: 'radial-gradient(circle, rgba(79,107,255,0.12) 0%, transparent 65%)' }}
              />
              <svg width="480" height="480" viewBox="0 0 480 480" fill="none" className="w-[300px] h-[300px] md:w-[480px] md:h-[480px]">
                <circle cx="240" cy="240" r="230" stroke="rgba(79,107,255,0.16)" strokeWidth="1" />
                <circle cx="240" cy="240" r="165" stroke="rgba(79,107,255,0.11)" strokeWidth="1" />
                <circle cx="240" cy="240" r="90" stroke="rgba(79,107,255,0.07)" strokeWidth="1" />
              </svg>
            </div>

            {/* Right circle with animation and orbiting dot */}
            <motion.div
              className="absolute pointer-events-none"
              style={{ bottom: '2%', right: '-8%' }}
              animate={{ x: [0, -12, 5, 0], y: [0, -10, 8, 0], scale: [1, 1.05, 0.97, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[220px] h-[220px] md:w-[310px] md:h-[310px] rounded-full blur-[80px]"
                style={{ background: 'radial-gradient(circle, rgba(123,97,255,0.18) 0%, transparent 65%)' }}
              />
              <svg width="360" height="360" viewBox="0 0 360 360" fill="none" className="w-[225px] h-[225px] md:w-[360px] md:h-[360px]">
                <circle cx="180" cy="180" r="172" stroke="rgba(123,97,255,0.22)" strokeWidth="1.2" />
                <circle cx="180" cy="180" r="120" stroke="rgba(123,97,255,0.16)" strokeWidth="1" />
                <circle cx="180" cy="180" r="60" stroke="rgba(123,97,255,0.10)" strokeWidth="1" />
                <circle r="4" fill="rgba(123,97,255,0.55)">
                  <animateMotion dur="18s" repeatCount="indefinite" path="M 180 8 A 172 172 0 0 1 352 180 A 172 172 0 0 1 180 352 A 172 172 0 0 1 8 180 A 172 172 0 0 1 180 8" />
                  <animate attributeName="opacity" values="0.55;0.25;0.55" dur="3s" repeatCount="indefinite" />
                </circle>
              </svg>
            </motion.div>

            {/* Central glow */}
            <div className="absolute bottom-[14%] left-1/2 -translate-x-1/2 pointer-events-none">
              <div className="w-[400px] h-[400px] md:w-[550px] md:h-[550px] rounded-full blur-[120px]"
                style={{ background: 'radial-gradient(circle at center, rgba(79,107,255,0.22) 0%, rgba(123,97,255,0.12) 35%, transparent 70%)' }}
              />
            </div>

            <div className="relative z-10">
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="font-body text-[30px] sm:text-[45px] tracking-tight leading-tight font-semibold text-[#08122a] mb-[21px]"
              >
                Connect with us
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 25 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="font-body text-base md:text-lg leading-relaxed text-[#475569] max-w-xl mx-auto mb-8"
              >
                For investment inquiries and general questions, please contact our team.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={ctaInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
              >
                <a
                  href="/contact"
                  data-testid="about-cta-button"
                  className="connect-cta-btn group inline-flex items-center gap-2 px-[18px] py-[10px] text-white text-[14px] font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5"
                >
                  Submit an inquiry
                  <ArrowUpRight className="w-[16px] h-[16px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
